/**
 * TestimonialsTab - Customer testimonials management
 */
import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveImageUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

export function TestimonialsTab({ content, setContent }) {
  const [uploadingAvatarIndex, setUploadingAvatarIndex] = useState(null);

  const updateTestimonial = (index, field, value) => {
    setContent(prev => {
      const newTestimonials = [...(prev.testimonials || [])];
      newTestimonials[index] = { ...newTestimonials[index], [field]: value };
      return { ...prev, testimonials: newTestimonials };
    });
  };

  const updateHeading = (field, value) => {
    setContent(prev => ({
      ...prev,
      testimonials_heading: { ...(prev.testimonials_heading || {}), [field]: value }
    }));
  };

  const addTestimonial = () => {
    setContent(prev => ({
      ...prev,
      testimonials: [...(prev.testimonials || []), {
        id: Date.now().toString(), rating: 5, text: 'Testimonial text goes here.',
        author: 'Author Name', role: 'Role, Team Name', avatar: ''
      }]
    }));
  };

  const removeTestimonial = (index) => {
    setContent(prev => ({ ...prev, testimonials: prev.testimonials.filter((_, i) => i !== index) }));
  };

  const handleAvatarUpload = async (index, file) => {
    if (!file) return;
    setUploadingAvatarIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateTestimonial(index, 'avatar', res.data.image_url);
    } catch (err) {
      console.error('Testimonial avatar upload failed:', err);
    } finally {
      setUploadingAvatarIndex(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Testimonials</CardTitle>
          <CardDescription>Manage customer testimonials</CardDescription>
        </div>
        <Button onClick={addTestimonial} variant="outline"><Plus className="w-4 h-4 mr-2" />Add Testimonial</Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section Title & Subtitle */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <span className="text-xs font-medium text-gray-500 block">Section Heading</span>
          <div>
            <Label>Badge Text</Label>
            <Input
              value={content.testimonials_heading?.badge || ''}
              onChange={(e) => updateHeading('badge', e.target.value)}
              placeholder="e.g., TESTIMONIALS"
              data-testid="testimonials-heading-badge"
            />
          </div>
          <div>
            <Label>Title</Label>
            <Input
              value={content.testimonials_heading?.title || ''}
              onChange={(e) => updateHeading('title', e.target.value)}
              placeholder="e.g., Loved by Grassroots Coaches"
              data-testid="testimonials-heading-title"
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.testimonials_heading?.subtitle || ''}
              onChange={(e) => updateHeading('subtitle', e.target.value)}
              placeholder="e.g., See what coaches are saying"
              data-testid="testimonials-heading-subtitle"
            />
          </div>
        </div>
        {content.testimonials?.map((testimonial, index) => (
          <div key={testimonial.id || index} className="bg-gray-50 p-6 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Testimonial {index + 1}</span>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => removeTestimonial(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={testimonial.rating} onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))} /></div>
            <div><Label>Testimonial Text</Label><Textarea value={testimonial.text} onChange={(e) => updateTestimonial(index, 'text', e.target.value)} rows={3} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Author Name</Label><Input value={testimonial.author} onChange={(e) => updateTestimonial(index, 'author', e.target.value)} /></div>
              <div><Label>Author Role/Team</Label><Input value={testimonial.role} onChange={(e) => updateTestimonial(index, 'role', e.target.value)} /></div>
            </div>

            <div>
              <Label>Club Badge / Avatar</Label>
              <div className="flex items-center gap-3 mt-1">
                {testimonial.avatar ? (
                  <img
                    src={resolveImageUrl(testimonial.avatar)}
                    alt={testimonial.author || `Testimonial ${index + 1}`}
                    className="w-12 h-12 object-contain bg-white border border-gray-200 rounded"
                    data-testid={`testimonial-avatar-preview-${index}`}
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400 text-center leading-tight">
                    Default<br />avatar
                  </div>
                )}
                <label
                  className={`flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors ${uploadingAvatarIndex === index ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid={`testimonial-avatar-upload-${index}`}
                >
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {uploadingAvatarIndex === index ? 'Uploading...' : testimonial.avatar ? 'Replace badge' : 'Upload badge'}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      handleAvatarUpload(index, e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
                {testimonial.avatar && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateTestimonial(index, 'avatar', '')}
                    className="text-xs text-gray-500 hover:text-red-600"
                    data-testid={`testimonial-avatar-remove-${index}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Leave blank to use the default placeholder icon.</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
